import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import useLanguage from '../hooks/useLanguage';

const TradingModal = ({ isOpen, onClose, type, symbol }) => {
  const [activeTab, setActiveTab] = useState('market');
  const [volume, setVolume] = useState('0.01');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [price, setPrice] = useState('');
  const { t, isRTL, forceUpdate } = useLanguage();

  // Force re-render when language changes
  useEffect(() => {
    // Component will re-render when forceUpdate changes
  }, [forceUpdate]);

  if (!isOpen) return null;

  const isBuy = type === 'buy';
  const currentPrice = isBuy ? symbol?.ask : symbol?.bid;

  const handlePlaceOrder = async () => {
    // Simulate order placement with proper endpoint call
    const orderData = {
      type: activeTab === 'market' ? 'market' : 'pending',
      action: type,
      symbol: symbol?.symbol,
      volume: parseFloat(volume),
      price: activeTab === 'market' ? currentPrice : parseFloat(price),
      stopLoss: stopLoss ? parseFloat(stopLoss) : null,
      takeProfit: takeProfit ? parseFloat(takeProfit) : null,
      timestamp: new Date()?.toISOString()
    };
    
    console.log('Placing order:', orderData);
    
    // TODO: Replace with actual API endpoint call
    try {
      // Example API call structure:
      // const response = await fetch('/api/trading/orders', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(orderData)
      // });
      // const result = await response.json();
      
      // For now, simulate success
      console.log('Order placed successfully:', orderData);
    } catch (error) {
      console.error('Failed to place order:', error);
    }
    
    onClose();
  };

  // Dynamic modal title based on trade type and language
  const getModalTitle = () => {
    const orderType = isBuy ? t('buyOrder') : t('sellOrder');
    return `${orderType} - ${symbol?.symbol || 'EURUSD'}`;
  };

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 ${isRTL ? 'rtl' : ''}`}>
      <div className="bg-card rounded-lg shadow-lg w-full max-w-md">
        {/* Modal Header - Dynamic based on trade type and language */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">
            {getModalTitle()}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Modal Tabs - Dynamically translated */}
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab('market')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'market' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('market')}
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'pending' ?'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t('pending')}
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-4 space-y-4">
          {/* Current Price */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('currentPrice')}:</span>
            <span className="font-mono font-medium text-foreground">
              {currentPrice?.toFixed(symbol?.symbol?.includes('USD') && !symbol?.symbol?.startsWith('USD') ? 5 : 2)}
            </span>
          </div>

          {/* Volume Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('volume')} ({t('lot')})
            </label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={volume}
              onChange={(e) => setVolume(e?.target?.value)}
              placeholder="0.01"
              required
            />
          </div>

          {/* Price Input - Only for Pending Orders */}
          {activeTab === 'pending' && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('executionPrice')}
              </label>
              <Input
                type="number"
                step="0.00001"
                value={price}
                onChange={(e) => setPrice(e?.target?.value)}
                placeholder={currentPrice?.toFixed(5)}
                required
              />
            </div>
          )}

          {/* Stop Loss */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('stopLoss')} ({t('optional')})
            </label>
            <Input
              type="number"
              step="0.00001"
              value={stopLoss}
              onChange={(e) => setStopLoss(e?.target?.value)}
              placeholder="0.00000"
            />
          </div>

          {/* Take Profit */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t('takeProfit')} ({t('optional')})
            </label>
            <Input
              type="number"
              step="0.00001"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e?.target?.value)}
              placeholder="0.00000"
            />
          </div>

          {/* Order Summary */}
          <div className="bg-muted p-3 rounded space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('symbol')}:</span>
              <span className="font-medium">{symbol?.symbol}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('type')}:</span>
              <span className="font-medium">
                {activeTab === 'market' ? t('marketOrder') : t('pendingOrder')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('volume')}:</span>
              <span className="font-medium">{volume} {t('lot')}</span>
            </div>
            {activeTab === 'market' && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('executionPrice')}:</span>
                <span className="font-medium font-mono">{currentPrice?.toFixed(5)}</span>
              </div>
            )}
          </div>

          {/* Action Buttons - Dynamic styling and text based on trade type */}
          <div className="flex space-x-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handlePlaceOrder}
              className={`flex-1 ${
                isBuy 
                  ? 'bg-green-600 hover:bg-green-700 text-white' :'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              {isBuy ? t('buy') : t('sell')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingModal;